import React, {useEffect, useState} from "react";
import {useParams} from "react-router";
import axiosInstance from "../../api";
import Spinner from "../../components/Spinner";
import {Box, Grid, Link, Paper} from "@material-ui/core";
import Typography from "@material-ui/core/Typography";
import {Alert} from "@material-ui/lab";
import Button from "@material-ui/core/Button";
import {MUIInput} from "../../utils/formUtils";
import {useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import * as yup from "yup";
import AppDetails from "./AppDetails";
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

const validationSchema = yup.object().shape({
    addressLine1: yup.string().required('Please complete all required fields').nullable(),
    email: yup.string().email('Email should be in valid format').required('Please complete all required fields').nullable()
});

export default function RetrieveApplicationDetails() {
    const {id} = useParams();
    const [showSpinner, setShowSpinner] = useState(false);
    const [fetchData, setFetchData] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [consumerData, setConsumerData] = useState({});
    const [addressLine1, setAddressLine1] = useState('');
    const [addressLine2, setAddressLine2] = useState('');
    const [emailAddress, setEmailAddress] = useState('');
    const [hasMultiple, setHasMultiple] = useState(false);
    const [allData, setAllData] = useState([]);
    const [rows, setRows] = useState([]);
    const {handleSubmit, control, trigger, formState: {errors}} = useForm({resolver: yupResolver(validationSchema)});

    useEffect(() => {
        if (id) {
            setShowSpinner(true);
            setErrorMessage(undefined);
            axiosInstance.get("unauth/leads/retrieveUserRequest?id=" + id)
                .then(result => {
                    setFetchData(true);
                    setConsumerData(result);
                })
                .catch(err => {
                    setFetchData(false);
                    setErrorMessage('An error occured while retrieving the request. Please provide the Address and email details to proceed further.');
                }).finally(() => setShowSpinner(false));
        } else {
            setFetchData(false);
        }
    }, [id]);

    const onSubmit = data => {
        setShowSpinner(true);
        setErrorMessage(null);
        axiosInstance.post("unauth/leads/retrieveApplication", data)
            .then(data => {
                if (data && data.length === 1) {
                    setConsumerData(data[0]);
                    setFetchData(true);
                    setErrorMessage(null);
                    setHasMultiple(false);
                } else {
                    setHasMultiple(true);
                    setFetchData(false);
                    setAllData(data);
                    let rows = data.map(k => {
                        let address = k.addressLine1 + (k.addressLine2 ? k.addressLine2 + ', ' : ', ') + k.city + ', ' + k.state + ', ' + k.zip;
                        return {'propertyAddress': address, 'email': k.email, 'id': k.userRequestId};
                    });
                    setRows(rows);
                }
            })
            .catch(err => {
                if (err && err.response && err.response.data === false) {
                    setErrorMessage('The email and address combination is not valid. Please try again.');
                } else {
                    setErrorMessage('An error occurred. Please try again later.');
                }
            })
            .finally(() => setShowSpinner(false));
    };

    const showRequest = (id) => {
        setConsumerData(allData.filter(x => x.userRequestId === id)[0]);
        setFetchData(true);
    }

    const updateData = (data) => {
        if (data && data.hasOwnProperty('userRequestStatus')) {
            let updatedData = Object.assign({}, allData.filter(x => x.userRequestId === data.userRequestId)[0], {userRequestStatus: data.userRequestStatus});
            setAllData(allData.map(x => {
                return x.userRequestId === data.userRequestId ? updatedData : x
            }));
        }

        if (data && data.hasOwnProperty('coverageDate')) {
            let updatedData = Object.assign({}, allData.filter(x => x.userRequestId === data.userRequestId)[0], {coverageStartDate: data.coverageDate});
            setAllData(allData.map(x => {
                return x.userRequestId === data.userRequestId ? updatedData : x
            }));
        }


    }

    if (showSpinner) return <Spinner/>

    return (
        <>
            {fetchData &&
            <AppDetails consumerData={consumerData} setConsumerData={setConsumerData} control={control} errors={errors}
                        hasMultiple={hasMultiple} setFetchData={setFetchData} updateData={updateData}/>
            }
            {!fetchData &&
            <Box m={3}>
                <section>
                    <Typography variant="h4" align="left" component="h4" gutterBottom style={{fontWeight: 600}}>
                        Retrieve Application
                    </Typography>
                    <form
                        onSubmit={handleSubmit(onSubmit)}>
                        <Paper style={{padding: 16}}>
                            <Grid container alignItems="flex-start" spacing={2}>
                                <Grid item xs={12} sm={4} md={4}>
                                    <MUIInput control={control} name="addressLine1" inputPrp={{maxLength: 150}}
                                              defaultValue={addressLine1}
                                              value={addressLine1} label="Address Line 1"
                                              onInputChange={data => setAddressLine1(data.target.value)}
                                              errors={errors}/>
                                </Grid>
                                <Grid item xs={12} sm={4} md={4}>
                                    <MUIInput control={control} name="addressLine2" inputPrp={{maxLength: 150}}
                                              defaultValue={addressLine2}
                                              value={addressLine2} label="Address Line 2"
                                              onInputChange={data => setAddressLine2(data.target.value)}
                                              errors={errors}/>
                                </Grid>
                                <Grid item xs={12} sm={4} md={4}>
                                    <MUIInput control={control} name="email" inputPrp={{maxLength: 100}}
                                              defaultValue={emailAddress}
                                              value={emailAddress} label="Email Address"
                                              onInputChange={data => setEmailAddress(data.target.value)}
                                              errors={errors}/>
                                </Grid>
                                <Grid item xs={12} sm={4} md={4}>
                                    <Box component="span" justifyContent="flex-start" alignItems="flex-start"
                                         display="flex">
                                        <br/><br/>
                                        <Button type="submit" variant="contained" color="primary" name="btnNext">
                                            Retrieve
                                        </Button>
                                    </Box>
                                </Grid>
                            </Grid>
                            <br/>
                            <br/>
                            {hasMultiple &&
                            <>
                                <Typography variant="h4" align="left" component="h4" gutterBottom>
                                    Requests
                                </Typography>
                                <TableContainer component={Paper}>
                                    <Table size="small" aria-label="a dense table">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Request Id</TableCell>
                                                <TableCell>Property Address</TableCell>
                                                <TableCell>Email</TableCell>
                                                <TableCell>Action</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {rows.map((row) => (
                                                <TableRow
                                                    key={row.id}
                                                    sx={{'&:last-child td, &:last-child th': {border: 0}}}
                                                >
                                                    <TableCell component="th" scope="row">
                                                        {row.id}
                                                    </TableCell>
                                                    <TableCell component="th" scope="row">
                                                        {row.propertyAddress}
                                                    </TableCell>
                                                    <TableCell>{row.email}</TableCell>
                                                    <TableCell><Link
                                                        onClick={() => showRequest(row.id)}>view</Link></TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </>
                            }
                        </Paper>
                    </form>
                </section>
            </Box>
            }

            <Box>
                {errorMessage && <div>
                    <br/>
                    <Alert variant="filled" severity="error">{errorMessage}</Alert>
                </div>}
            </Box>


        </>
    );
}
